import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ShieldCheck, ShieldOff } from 'lucide-react'
import { useSetup2FA, useEnable2FA, useDisable2FA } from '@/application/services/user.service'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/ui/components/common/Card'
import { Button } from '@/ui/components/common/Button'
import { Input } from '@/ui/components/forms/Input'
import { Badge } from '@/ui/components/common/Badge'

type TwoFAStep = 'idle' | 'setup' | 'disable'

interface TwoFASectionProps {
  twoFactorEnabled: boolean
}

/**
 * Two-Factor Authentication section: shows enabled/disabled badge, drives the
 * QR-code setup flow and the disable-confirmation flow. Owns its own step
 * state, the captured TOTP code, and the setup/enable/disable mutations.
 */
export function TwoFASection({ twoFactorEnabled }: TwoFASectionProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState<TwoFAStep>('idle')
  const [qrData, setQrData] = useState<{ qrCodeDataUrl: string; secret: string } | null>(null)
  const [totp, setTotp] = useState('')

  const { mutateAsync: setup2FA, isPending: isSettingUp } = useSetup2FA()
  const { mutateAsync: enable2FA, isPending: isEnabling } = useEnable2FA()
  const { mutateAsync: disable2FA, isPending: isDisabling } = useDisable2FA()

  const handleStartSetup = async () => {
    try {
      const data = await setup2FA()
      setQrData({ qrCodeDataUrl: data.qrCodeDataUrl, secret: data.secret })
      setTotp('')
      setStep('setup')
    } catch {
      toast.error(t('admin.profile_2fa_error'))
    }
  }

  const handleEnable = async () => {
    try {
      await enable2FA(totp)
      toast.success(t('admin.profile_2fa_enabled_success'))
      setStep('idle')
      setQrData(null)
      setTotp('')
    } catch {
      toast.error(t('admin.profile_2fa_error'))
    }
  }

  const handleDisable = async () => {
    try {
      await disable2FA(totp)
      toast.success(t('admin.profile_2fa_disabled_success'))
      setStep('idle')
      setTotp('')
    } catch {
      toast.error(t('admin.profile_2fa_error'))
    }
  }

  const handleCancel = () => {
    setStep('idle')
    setQrData(null)
    setTotp('')
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('admin.profile_2fa_title')}</CardTitle>
            <CardDescription className="mt-1">{t('admin.profile_2fa_desc')}</CardDescription>
          </div>
          <Badge variant={twoFactorEnabled ? 'default' : 'secondary'} className="shrink-0">
            {twoFactorEnabled ? (
              <>
                <ShieldCheck className="w-3 h-3 mr-1" />
                {t('admin.profile_2fa_status_enabled')}
              </>
            ) : (
              <>
                <ShieldOff className="w-3 h-3 mr-1" />
                {t('admin.profile_2fa_status_disabled')}
              </>
            )}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* ── Idle state ── */}
        {step === 'idle' && (
          <>
            {!twoFactorEnabled ? (
              <Button onClick={handleStartSetup} isLoading={isSettingUp} variant="outline">
                <ShieldCheck className="w-4 h-4 mr-2" />
                {t('admin.profile_2fa_enable_btn')}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setTotp('')
                  setStep('disable')
                }}
                variant="destructive"
              >
                <ShieldOff className="w-4 h-4 mr-2" />
                {t('admin.profile_2fa_disable_btn')}
              </Button>
            )}
          </>
        )}

        {/* ── Setup (QR code + first TOTP confirmation) ── */}
        {step === 'setup' && qrData && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('admin.profile_2fa_setup_instruction')}
            </p>
            <div className="flex justify-center">
              <img
                src={qrData.qrCodeDataUrl}
                alt="QR Code"
                className="w-44 h-44 rounded-lg border border-border"
              />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                {t('admin.profile_2fa_manual_entry')}
              </p>
              <code className="block text-xs bg-muted px-3 py-2 rounded-md font-mono break-all">
                {qrData.secret}
              </code>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('admin.profile_2fa_confirm_label')}</label>
              <Input
                value={totp}
                onChange={(event) => setTotp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder={t('admin.profile_2fa_confirm_placeholder')}
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="ghost" onClick={handleCancel}>
                {t('admin.profile_2fa_cancel')}
              </Button>
              <Button onClick={handleEnable} isLoading={isEnabling} disabled={totp.length !== 6}>
                {t('admin.profile_2fa_confirm_btn')}
              </Button>
            </div>
          </div>
        )}

        {/* ── Disable confirmation ── */}
        {step === 'disable' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('admin.profile_2fa_disable_instruction')}
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('admin.profile_2fa_confirm_label')}</label>
              <Input
                value={totp}
                onChange={(event) => setTotp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder={t('admin.profile_2fa_confirm_placeholder')}
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="ghost" onClick={handleCancel}>
                {t('admin.profile_2fa_cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDisable}
                isLoading={isDisabling}
                disabled={totp.length !== 6}
              >
                {t('admin.profile_2fa_disable_confirm_btn')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
